import { formatNumber } from "./number-helper";

const convert = (val, prevUnit, curUnit, format = true, precision = 2) => {
  if (val === null || val === undefined) {
    return null;
  }
  let newVal = val;

  // Absolute temperature conversions (with offset)
  if (prevUnit === "C") {
    if (curUnit === "F") {
      newVal = ((1.0 * newVal) / 5) * 9 + 32;
    }
  } else if (prevUnit === "F") {
    if (curUnit === "C") {
      newVal = ((newVal - 32) / 9) * 5;
    }
  }
  // Temperature difference conversions (scaling only, no offset)
  else if (prevUnit === "dC") {
    if (curUnit === "dF") {
      newVal = (newVal * 9) / 5;
    }
  } else if (prevUnit === "dF") {
    if (curUnit === "dC") {
      newVal = (newVal * 5) / 9;
    }
  }
  // Time conversions
  else if (
    prevUnit === "ms" ||
    prevUnit === "sec" ||
    prevUnit === "min" ||
    prevUnit === "hour" ||
    prevUnit === "day"
  ) {
    if (prevUnit === "sec") {
      newVal = newVal * 1000;
    } else if (prevUnit === "min") {
      newVal = newVal * 1000 * 60;
    } else if (prevUnit === "hour") {
      newVal = newVal * 1000 * 60 * 60;
    } else if (prevUnit === "day") {
      newVal = newVal * 1000 * 60 * 60 * 24;
    }
    if (curUnit === "sec") {
      newVal = newVal / 1000;
    } else if (curUnit === "min") {
      newVal = newVal / 1000 / 60;
    } else if (curUnit === "hour") {
      newVal = newVal / 1000 / 60 / 60;
    } else if (curUnit === "day") {
      newVal = newVal / 1000 / 60 / 60 / 24;
    }
  }
  // Weight conversions (convert to grams as base unit)
  else if (
    prevUnit === "kg" ||
    prevUnit === "g" ||
    prevUnit === "lb" ||
    prevUnit === "oz" ||
    prevUnit === "mg"
  ) {
    // Convert from previous unit to grams
    if (prevUnit === "kg") {
      newVal = newVal * 1000;
    } else if (prevUnit === "lb") {
      newVal = newVal * 453.592;
    } else if (prevUnit === "oz") {
      newVal = newVal * 28.3495;
    } else if (prevUnit === "mg") {
      newVal = newVal / 1000;
    }
    // g stays as is (base unit)

    // Convert from grams to target unit
    if (curUnit === "kg") {
      newVal = newVal / 1000;
    } else if (curUnit === "lb") {
      newVal = newVal / 453.592;
    } else if (curUnit === "oz") {
      newVal = newVal / 28.3495;
    } else if (curUnit === "mg") {
      newVal = newVal * 1000;
    }
    // g stays as is (base unit)
  }
  // Length conversions (convert to millimeters as base unit)
  else if (
    prevUnit === "mm" ||
    prevUnit === "m" ||
    prevUnit === "cm" ||
    prevUnit === "km" ||
    prevUnit === "inch" ||
    prevUnit === "foot" ||
    prevUnit === "yard" ||
    prevUnit === "mile"
  ) {
    // Convert from previous unit to millimeters
    if (prevUnit === "m") {
      newVal = newVal * 1000;
    } else if (prevUnit === "cm") {
      newVal = newVal * 10;
    } else if (prevUnit === "km") {
      newVal = newVal * 1000000;
    } else if (prevUnit === "inch") {
      newVal = newVal * 25.4;
    } else if (prevUnit === "foot") {
      newVal = newVal * 304.8;
    } else if (prevUnit === "yard") {
      newVal = newVal * 914.4;
    } else if (prevUnit === "mile") {
      newVal = newVal * 1609344;
    }
    // mm stays as is (base unit)

    // Convert from millimeters to target unit
    if (curUnit === "m") {
      newVal = newVal / 1000;
    } else if (curUnit === "cm") {
      newVal = newVal / 10;
    } else if (curUnit === "km") {
      newVal = newVal / 1000000;
    } else if (curUnit === "inch") {
      newVal = newVal / 25.4;
    } else if (curUnit === "foot") {
      newVal = newVal / 304.8;
    } else if (curUnit === "yard") {
      newVal = newVal / 914.4;
    } else if (curUnit === "mile") {
      newVal = newVal / 1609344;
    }
    // mm stays as is (base unit)
  }

  return format ? formatNumber(newVal, precision) : newVal;
};

const normalizeByUnit = (
  value,
  currentUnit,
  dataType,
  min,
  max,
  precision = 2
) => {
  const regex = /^(-?[\d.]+)([a-zA-Z]+)$/;
  const match = regex.exec(value);

  if (match && match.length === 3) {
    // unit value
    const number = formatNumber(parseFloat(match[1]), precision);
    const unit = match[2];

    if (unit === currentUnit) {
      return { number, unit, min, max };
    }

    const newMin = convert(min, currentUnit, unit, dataType, false, precision);
    const newMax = convert(max, currentUnit, unit, dataType, false, precision);

    return { number, unit, min: newMin, max: newMax };
  } else {
    // no unit value
    const number = formatNumber(Number(value), precision);
    return { number, unit: currentUnit, min, max };
  }
};

/**
 * Convert a value with unit string to a formatted string with desired unit
 * @param {string} valueWithUnit - Value with unit (e.g., "44.80000009536743F")
 * @param {string} targetUnit - Desired unit (e.g., "C")
 * @param {number} precision - Number of decimal places (default: 0 for whole numbers)
 * @returns {string} Formatted value with unit (e.g., "10°C")
 */
const convertToFormattedUnit = (valueWithUnit, targetUnit, precision = 0) => {
  if (!valueWithUnit) {
    return null;
  }

  // Extract number and unit from the input string
  const regex = /^(-?[\d.]+)([a-zA-Z°]+)$/;
  const match = regex.exec(valueWithUnit.trim());

  if (!match || match.length !== 3) {
    // If no unit found, assume it's already in target unit
    const number = parseFloat(valueWithUnit);
    if (isNaN(number)) {
      return null;
    }
    return formatValueWithUnit(number, targetUnit, precision);
  }

  const value = parseFloat(match[1]);
  const sourceUnit = match[2].replace("°", ""); // Remove degree symbol if present

  if (isNaN(value)) {
    return null;
  }

  // Convert to target unit
  const convertedValue = convert(
    value,
    sourceUnit,
    targetUnit,
    false,
    precision
  );

  return formatValueWithUnit(convertedValue, targetUnit, precision);
};

/**
 * Format a numeric value with its unit symbol
 * @param {number} value - Numeric value
 * @param {string} unit - Unit string
 * @param {number} precision - Number of decimal places
 * @returns {string} Formatted string with unit
 */
const formatValueWithUnit = (value, unit, precision = 0) => {
  const roundedValue = formatNumber(value, precision);

  // Temperature units get degree symbol
  if (unit === "C" || unit === "F") {
    return `${roundedValue}°${unit}`;
  }

  // Temperature difference units with delta symbol
  if (unit === "dC") {
    return `${roundedValue}Δ°C`;
  }
  if (unit === "dF") {
    return `${roundedValue}Δ°F`;
  }

  // Time units
  if (unit === "ms") return `${roundedValue}ms`;
  if (unit === "sec") return `${roundedValue}s`;
  if (unit === "min") return `${roundedValue}min`;
  if (unit === "hour") return `${roundedValue}h`;
  if (unit === "day") return `${roundedValue}d`;

  // Weight units
  if (unit === "kg") return `${roundedValue}kg`;
  if (unit === "g") return `${roundedValue}g`;
  if (unit === "mg") return `${roundedValue}mg`;
  if (unit === "lb") return `${roundedValue}lb`;
  if (unit === "oz") return `${roundedValue}oz`;

  // Length units
  if (unit === "mm") return `${roundedValue}mm`;
  if (unit === "cm") return `${roundedValue}cm`;
  if (unit === "m") return `${roundedValue}m`;
  if (unit === "km") return `${roundedValue}km`;
  if (unit === "inch") return `${roundedValue}in`;
  if (unit === "foot") return `${roundedValue}ft`;
  if (unit === "yard") return `${roundedValue}yd`;
  if (unit === "mile") return `${roundedValue}mi`;

  // Default: just append the unit
  return `${roundedValue}${unit}`;
};

export { convert, normalizeByUnit, convertToFormattedUnit };
